import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Check, X } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useAuth } from "@/hooks/use-auth";

export const Route = createFileRoute("/versus")({
  head: () => ({
    meta: [
      { title: "Tiger's Eye vs AI writing tools — critique, not ghostwriting" },
      {
        name: "description",
        content:
          "AI writing tools generate text for you — which can cost you your degree. Tiger's Eye only critiques your own draft, so your work and your grade stay yours.",
      },
      { property: "og:url", content: "https://tigerseye.app/versus" },
      {
        property: "og:title",
        content: "Tiger's Eye vs AI writing tools — critique, not ghostwriting",
      },
      {
        property: "og:description",
        content:
          "AI writing tools generate text for you — which can cost you your degree. Tiger's Eye only critiques your own draft, so your work and your grade stay yours.",
      },
    ],
    links: [{ rel: "canonical", href: "https://tigerseye.app/versus" }],
  }),
  component: VersusPage,
});

const BG = "var(--paper)";
const SURFACE = "var(--card)";
const INK = "var(--primary)";
const INK_SOFT = "var(--ink-secondary)";
const INK_FAINT = "var(--ink-tertiary)";
const BRASS = "var(--editorial-accent, var(--accent))";
const RULE = "var(--rule)";
const ALERT = "var(--destructive)";

const SERIF =
  '"Iowan Old Style", "Source Serif Pro", "Source Serif 4", "Charter", Georgia, ui-serif, serif';

const ROWS: { feature: string; tiger: boolean; writers: boolean; note?: string }[] = [
  { feature: "Writes your essay for you", tiger: false, writers: true },
  { feature: "Rewrites your paragraphs", tiger: false, writers: true },
  { feature: "Critiques your own draft", tiger: true, writers: false },
  { feature: "Checks against your assignment brief", tiger: true, writers: false },
  { feature: "Checks your referencing style", tiger: true, writers: false },
  { feature: "Safe under academic integrity rules", tiger: true, writers: false, note: "Check your university's policy" },
  { feature: "Your voice and ideas stay yours", tiger: true, writers: false },
  { feature: "Helps you become a better writer", tiger: true, writers: false },
];

export default function VersusPage() {
  const { user } = useAuth();
  const ctaTo = user ? "/reviews/new" : "/signup";

  return (
    <main className="min-h-screen w-full" style={{ background: BG, color: INK }}>
      <style>{`
        .hv4-serif { font-family: ${SERIF}; font-feature-settings: "liga","kern"; }
        .hv4-eyebrow {
          font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase;
          font-weight: 600; color: ${BRASS};
        }
        .hv4-cta { transition: transform .18s ease, box-shadow .18s ease, background .18s ease; }
        .hv4-cta-primary { background: ${INK}; color: ${BG}; }
        .hv4-cta-primary:hover { transform: translateY(-1px); box-shadow: 0 10px 22px -14px ${INK}; background: color-mix(in oklch, ${INK} 85%, black); }
      `}</style>

      <header style={{ borderBottom: `1px solid ${RULE}` }}>
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/" className="hv4-serif text-lg font-semibold" style={{ color: INK }}>
            Tiger&apos;s Eye
          </Link>
          <div className="flex items-center gap-4 text-sm">
            <Link to="/pricing" style={{ color: INK_SOFT }}>
              Pricing
            </Link>
            {user ? (
              <Link to="/dashboard" style={{ color: INK_SOFT }}>
                Dashboard
              </Link>
            ) : (
              <Link to="/login" style={{ color: INK_SOFT }}>
                Sign in
              </Link>
            )}
          </div>
        </div>
      </header>

      <section className="mx-auto max-w-3xl px-6 pb-24 pt-16">
        <Reveal>
          <p className="hv4-eyebrow">The honest difference</p>
          <h1 className="hv4-serif mt-4 text-balance text-4xl leading-tight sm:text-5xl">
            Critique, not ghostwriting
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed" style={{ color: INK_SOFT }}>
            AI writing tools generate text for you — and universities are failing
            students for it. Tiger&apos;s Eye does the opposite: it reads{" "}
            <em>your</em> draft and tells you how to make it better, like a
            thoughtful tutor would. The words stay yours. The grade stays yours.
          </p>
        </Reveal>

        <Reveal>
          <div
            className="mt-12 overflow-x-auto rounded-2xl"
            style={{ border: `1px solid ${RULE}` }}
          >
            <table className="w-full min-w-[520px] text-sm" style={{ background: SURFACE }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${RULE}` }}>
                  <th
                    className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wide"
                    style={{ color: INK_FAINT, width: "52%" }}
                  >
                    What it does
                  </th>
                  <th
                    className="px-4 py-4 text-center text-xs font-semibold uppercase tracking-wide"
                    style={{ color: BRASS, width: "24%" }}
                  >
                    Tiger&apos;s Eye
                  </th>
                  <th
                    className="px-4 py-4 text-center text-xs font-semibold uppercase tracking-wide"
                    style={{ color: INK_FAINT, width: "24%" }}
                  >
                    AI writing tools
                  </th>
                </tr>
              </thead>
              <tbody>
                {ROWS.map((row) => (
                  <tr key={row.feature} style={{ borderBottom: `1px solid ${RULE}` }}>
                    <td className="px-5 py-3.5" style={{ color: INK }}>
                      {row.feature}
                      {row.note && (
                        <span className="block text-xs" style={{ color: INK_FAINT }}>
                          {row.note}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3.5 text-center">
                      {row.tiger ? (
                        <Check className="mx-auto h-4 w-4" style={{ color: BRASS }} aria-label="Yes" />
                      ) : (
                        <X className="mx-auto h-4 w-4" style={{ color: ALERT }} aria-label="No" />
                      )}
                    </td>
                    <td className="px-4 py-3.5 text-center">
                      {row.writers ? (
                        <Check className="mx-auto h-4 w-4" style={{ color: INK_FAINT }} aria-label="Yes" />
                      ) : (
                        <X className="mx-auto h-4 w-4" style={{ color: ALERT }} aria-label="No" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>

        <Reveal>
          <div className="mt-12 rounded-2xl p-7" style={{ background: SURFACE, border: `1px solid ${RULE}` }}>
            <h2 className="hv4-serif text-2xl">Why it matters</h2>
            <p className="mt-3 text-sm leading-relaxed" style={{ color: INK_SOFT }}>
              Submitting AI-generated text as your own can count as contract
              cheating or plagiarism under most university rules — students have
              been failed, and worse, for it. Feedback on your own writing is a
              different thing entirely: it&apos;s how writers have always improved.
              Tiger&apos;s Eye gives you that feedback on demand, before you submit.
            </p>
            <p className="mt-3 text-sm leading-relaxed" style={{ color: INK_SOFT }}>
              Rules do vary between universities and modules, so always check your
              university&apos;s own policy on AI use.
            </p>
          </div>
        </Reveal>

        <Reveal>
          <div className="mt-12 text-center">
            <Link
              to={ctaTo}
              className="hv4-cta hv4-cta-primary inline-flex items-center gap-2 rounded-full px-7 py-4 text-base font-medium"
            >
              Start a free review
              <ArrowRight className="h-4 w-4" />
            </Link>
            <p className="mt-4 text-sm" style={{ color: INK_FAINT }}>
              More questions? <Link to="/faq" className="underline" style={{ color: INK_SOFT }}>Read the student FAQ</Link>
            </p>
          </div>
        </Reveal>
      </section>

      <footer
        className="mx-auto flex max-w-6xl items-center justify-between px-6 pb-10 text-xs"
        style={{ color: INK_FAINT }}
      >
        <span>© {new Date().getFullYear()} Tiger&apos;s Eye · by PRAXIA</span>
        <span className="flex items-center gap-4">
          <Link to="/pricing" style={{ color: INK_SOFT }}>
            Pricing
          </Link>
          <Link to="/terms" style={{ color: INK_SOFT }}>
            Terms &amp; ethos
          </Link>
        </span>
      </footer>
    </main>
  );
}
